import { Input } from '@base-ui/react/input';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';

interface FindStudyHelperProps {
  selectedId: number | null;
  onSelect: (id: number) => void;
  // Study helper ids to hide from the results (e.g. existing group members).
  excludeIds?: number[];
}

/**
 * A search-and-select widget for picking a study helper. Debounces the search
 * box and lists matches from `getStudyHelpers`; the caller owns the selected id.
 * Shared by the student-reassign dialog and the group "add study helper" dialog.
 */
export function FindStudyHelper({
  selectedId,
  onSelect,
  excludeIds = [],
}: FindStudyHelperProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = api.getStudyHelpers.useQuery({
    query: { limit: 20, offset: 0, search: debouncedSearch || undefined },
  });

  const excluded = new Set(excludeIds);
  const helpers = (data?.data ?? []).filter((h) => !excluded.has(h.id));

  return (
    <>
      <SearchInput
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
        placeholder={t('admin.studyHelpers.findPlaceholder')}
      />
      <HelperList>
        {isLoading ? (
          <EmptyMessage>{t('admin.common.loading')}</EmptyMessage>
        ) : helpers.length === 0 ? (
          <EmptyMessage>{t('admin.studyHelpers.noneFound')}</EmptyMessage>
        ) : (
          helpers.map((helper) => (
            <HelperItem
              key={helper.id}
              $selected={selectedId === helper.id}
              onClick={() => onSelect(helper.id)}
            >
              <HelperName>{helper.display_name}</HelperName>
              <HelperMeta>
                #{helper.id} · {helper.email} · {helper.country || '—'}
              </HelperMeta>
            </HelperItem>
          ))
        )}
      </HelperList>
    </>
  );
}

const SearchInput = styled(Input)`
  background-color: var(--grey-50);
  border: 1px solid var(--grey-300);
  border-radius: 0.375rem;
  color: var(--grey-900);
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  width: 100%;

  &:focus {
    outline: 2px solid var(--blue);
    outline-offset: -1px;
  }
`;

const HelperList = styled.div`
  border: 1px solid var(--grey-300);
  border-radius: 0.375rem;
  flex: 1 1 auto;
  margin-bottom: 1rem;
  max-height: 240px;
  min-height: 120px;
  overflow-y: auto;
`;

const HelperItem = styled.div<{ $selected: boolean }>`
  background-color: ${({ $selected }) =>
    $selected ? 'var(--blue-50, #e0f2fe)' : 'transparent'};
  border-bottom: 1px solid var(--grey-200);
  cursor: pointer;
  padding: 0.625rem 0.75rem;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ $selected }) =>
      $selected ? 'var(--blue-50, #e0f2fe)' : 'var(--grey-100)'};
  }
`;

const HelperName = styled.div`
  color: var(--grey-900);
  font-size: 0.875rem;
  font-weight: 500;
`;

const HelperMeta = styled.div`
  color: var(--grey-600);
  font-size: 0.75rem;
  margin-top: 0.125rem;
`;

const EmptyMessage = styled.div`
  color: var(--grey-600);
  font-size: 0.875rem;
  padding: 1rem;
  text-align: center;
`;
